import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Container, Heading, Icon, Screen, Spinner, Stack, Text, useToast } from '@fluent/ui';
import { CheckCircleGlyph } from '../profile/icons.js';
import { useAuth } from '../auth/authStore.js';
import { useSubscription } from './hooks.js';
import { derivedStatus, hasProAccess } from './access.js';
import { PaymentFrame, type FrameMode } from './PaymentFrame.js';
import { isNativeApp } from '../native/platform.js';
import { track } from '../lib/analytics.js';
import { LegalLinks } from '../legal/LegalLinks.js';
import './Paywall.css';

export interface PaywallProps {
  /** Lesson the user tried to open. Used for analytics-style "from where?"
   *  context after they convert (Phase 13). Optional. */
  fromLessonId?: string;
}

/**
 * Paywall — full-screen "you need Pro to play this lesson" screen.
 *
 * Two different screens, because the same code runs in two places
 * (DECISIONS 2026-09-21):
 *
 *   in a browser  → the pitch, and the SAME in-page card form as
 *                   /subscription (PaymentFrame). Not Paddle's pop-up —
 *                   Tornike 2026-09-21: the built-in form is clearer. The
 *                   website is where fluent.ge sells.
 *   in the app    → "this lesson is part of Pro" and nothing else. No price,
 *                   no button to buy, no link to the website. Google Play
 *                   cannot pay a seller in Georgia, and its policy forbids
 *                   steering app users to another way to pay.
 */
export function Paywall({ fromLessonId }: PaywallProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const subscription = useSubscription();
  const status = derivedStatus(subscription.data ?? null);
  const [frameMode, setFrameMode] = useState<FrameMode | null>(null);
  // After Paddle says "completed", the webhook still has to write our row.
  // 'waiting' polls for it; 'slow' is the honest "not confirmed yet" state.
  const [confirm, setConfirm] = useState<'idle' | 'waiting' | 'slow'>('idle');
  const native = isNativeApp();

  // Fire `paywall_shown` once per mount — exactly when the user is told
  // "this needs Pro". Critical for the conversion funnel.
  useEffect(() => {
    track('paywall_shown', {
      from_lesson_id: fromLessonId ?? null,
      status,
    });
  }, [fromLessonId, status]);

  // If their subscription becomes active mid-paywall (e.g. webhook landed
  // while we were rendering), bounce them back to where they came from.
  // Same rule as everywhere else — past_due stays HERE, on "fix your card".
  const entitled = hasProAccess(subscription.data ?? null);
  useEffect(() => {
    if (entitled) {
      const target = fromLessonId ? `/learn/${fromLessonId}` : '/';
      navigate(target, { replace: true });
    }
  }, [entitled, navigate, fromLessonId]);

  // Poll the subscription row every 2s for ~20s after checkout completes
  // (same budget as /subscription). The toast fires from HERE, not from the
  // redirect above: inside a lesson, LessonPlayer swaps this screen for the
  // lesson the instant the row turns entitled, so the redirect effect never
  // gets to run.
  const celebratedRef = useRef(false);
  useEffect(() => {
    if (confirm !== 'waiting') return;
    let ticks = 0;
    const id = window.setInterval(() => {
      ticks += 1;
      void subscription.refetch().then((r) => {
        if (hasProAccess(r.data ?? null) && !celebratedRef.current) {
          celebratedRef.current = true;
          toast({ tone: 'success', title: t('sub_page.toast.subscribed') });
        }
      });
      if (ticks >= 10) {
        window.clearInterval(id);
        setConfirm('slow');
      }
    }, 2000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- armed only on the 'waiting' edge
  }, [confirm]);

  if (native) {
    return (
      <Screen>
        <Container size="sm">
          <div className="fluent-paywall">
            <div className="fluent-paywall__hero" aria-hidden="true">🔒</div>
            <Heading level={1} style={{ textAlign: 'center' }}>
              {t('paywall.native_title')}
            </Heading>
            <Text size="lg" tone="muted" align="center">
              {t('paywall.native_body')}
            </Text>
            <Stack gap={2} style={{ marginTop: 24 }}>
              <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/')}>
                {t('paywall.back_home')}
              </Button>
            </Stack>
          </div>
        </Container>
      </Screen>
    );
  }

  // Status-aware copy. "none" gets the trial pitch; "expired" already had
  // its free week, so it pays today (no-trial price, paddle.ts); "past_due"
  // tells them to fix their card.
  const isPastDue = status === 'past_due';
  const isReturning = status === 'expired';

  return (
    <Screen>
      <Container size="sm">
        <div className="fluent-paywall">
          <div className="fluent-paywall__hero" aria-hidden="true">⚡</div>
          <Heading level={1} style={{ textAlign: 'center' }}>
            {isPastDue ? t('paywall.past_due_title') : t('paywall.title')}
          </Heading>
          <Text size="lg" tone="muted" align="center">
            {isPastDue
              ? t('paywall.past_due_subtitle')
              : isReturning
                ? t('paywall.subtitle_returning')
                : t('paywall.subtitle')}
          </Text>

          <ul className="fluent-paywall__perks">
            {['all_lessons', 'exam_tests', 'audio', 'streak_freeze', 'cancel_anytime'].map((perk) => (
              <li key={perk}>
                {/* A real tick. The bullet used to be a CSS circle with a bar
                    across it, which read as "no entry" on the one screen that
                    has to feel like a yes (Tornike's audit, 2026-09-22). */}
                <span className="fluent-paywall__perk-icon" aria-hidden="true">
                  <Icon size={22}>
                    <CheckCircleGlyph />
                  </Icon>
                </span>
                {t(`paywall.perk.${perk}`)}
              </li>
            ))}
          </ul>

          <Stack gap={3}>
            {confirm !== 'idle' ? (
              <div className="fluent-paywall__confirm" role="status">
                {confirm === 'waiting' ? (
                  <>
                    <Spinner size={18} />
                    <Text size="sm" weight="bold">{t('sub_page.pending')}</Text>
                  </>
                ) : (
                  <>
                    <Text size="sm" weight="bold">{t('sub_page.unknown.title')}</Text>
                    <Text size="sm" tone="muted" align="center">{t('sub_page.unknown.body')}</Text>
                    <Button variant="secondary" size="md" onClick={() => setConfirm('waiting')}>
                      {t('sub_page.unknown.recheck')}
                    </Button>
                  </>
                )}
              </div>
            ) : frameMode && user ? (
              <PaymentFrame
                mode={frameMode}
                email={user.email ?? ''}
                userId={user.id}
                trialOrPeriodEnd={null}
                onClose={() => setFrameMode(null)}
                onCompleted={() => {
                  setFrameMode(null);
                  setConfirm('waiting');
                }}
              />
            ) : (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() =>
                  setFrameMode(isPastDue ? 'fix' : isReturning ? 'resubscribe' : 'subscribe')
                }
              >
                {isPastDue
                  ? t('paywall.cta_fix_card')
                  : isReturning
                    ? t('sub_page.cta.resubscribe')
                    : t('paywall.cta_trial')}
              </Button>
            )}
            <Text size="sm" tone="soft" align="center">
              {isReturning ? t('paywall.fine_print_returning') : t('paywall.fine_print')}
            </Text>
            {/* The price is in dollars; a Georgian learner thinks in lari.
                Rounded range, no live FX (audit F007). */}
            <Text size="sm" tone="soft" align="center">
              {t('paywall.approx_gel')}
            </Text>
            <LegalLinks />
          </Stack>

          <Stack gap={2} style={{ marginTop: 24 }}>
            <Button variant="ghost" size="md" fullWidth onClick={() => navigate('/')}>
              {t('paywall.back_home')}
            </Button>
          </Stack>
        </div>
      </Container>
    </Screen>
  );
}
